import TR from 'utils/TR';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';
import HBox from 'components/common/HBox';
import Section from 'components/common/Section';
import SortButton from '../../../common/SortButton';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useSort from 'hooks/tables/useSort';
import PagingIndicator from 'components/common/paging/PagingIndicator';
import usePaging from 'hooks/tables/usePaging';
import defaultRenderPrevButtons from 'components/common/paging/defaultRenderPrevButtons';
import defaultRenderNextButtons from 'components/common/paging/defaultRenderNextButtons';
import Dot from 'components/common/Dot';
import VBox from 'components/common/VBox';
import CpfRatioBox from './CpfRatioBox';
import SORT_DESCENDING from 'constants/SORT_DESCENDING';
import useReportFoodAnalysis from 'hooks/communication/useReportFoodAnalysis';
import SORT_DEFAULT from 'constants/SORT_DEFAULT';

import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import CHECK_COLORS from 'constants/CHECK_COLORS';
import useSetChartData from 'hooks/app/useSetChartData';

const DEFAULT_SORT_LIST = [{ sort_by: '1', order_by: SORT_DESCENDING }];
const PAGE_ROW_CNT = 5;

export default function DietFoodAnalysis(props) {
  const {
    mealData,
    ...rest
  } = props;

  const {
    order: countOrder,
    onOrderChange: onCountOrderChange,
  } = useSort(false, SORT_DESCENDING);

  const {
    order: increasingOrder,
    onOrderChange: onIncreasingOrderChange,
  } = useSort();

  const {
    page,
    onPage,
    npage,
    onNPage,
    maxPage,
    onMaxPage
  } = usePaging({
    resetCondition: [],
    defaultMaxPage: 6,
  });

  const {
    requestReportFoodAnalysis,
  } = useReportFoodAnalysis();


  const [sort_list, setSortList] = React.useState(() => DEFAULT_SORT_LIST)
  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('DietFoodAnalysis.1', countOrder);
    const additionals = countOrder === SORT_DEFAULT ? [] : [{ sort_by: '1', order_by: countOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== '1')])
  }, [countOrder]);
  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('DietFoodAnalysis.1', increasingOrder);
    const additionals = increasingOrder === SORT_DEFAULT ? [] : [{ sort_by: '2', order_by: increasingOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== '2')])
  }, [increasingOrder]);

  const setChartData = useSetChartData('DietFoodAnalysisList');
  const [list, setList] = React.useState([]);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    requestReportFoodAnalysis({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      page_row_cnt: PAGE_ROW_CNT,
      page_no: npage,
      sort_list,
    }, (success, data) => {
      if (success) {
        setChartData(() => ({ list: data.food_analysis_list || [] }));
        setList(() => data.food_analysis_list || []);
        onMaxPage(data.max_page_no || 1);
        onPage(data.page_no || 1);
      } else {
        setChartData(() => ({ list: [] }));
        setList(() => []);
        onMaxPage(1);
        onPage(1);
        onNPage(1);
      }
    });
  }, [npage, onMaxPage, onNPage, onPage, patient_no, period_end, period_start, requestReportFoodAnalysis, setChartData, sort_list]);

  return (
    <Section
      {...ex('DietFoodAnalysis', rest)}
      {...{
        caption: T('대표음식별 분석'),
        direction: DIRECTION_VERTICAL,
      }}
    >
      <table>
        <thead>
          <tr>
            <th>
              <VBox>
                <div>
                  {T('대표음식')}
                </div>
              </VBox>
            </th>
            <th>
              <div>
                <VBox>
                  <div>
                    {T('기록(회)')}
                  </div>
                </VBox>
                <div>
                  <SortButton {...{ order: countOrder, onClick: onCountOrderChange }} />
                </div>
              </div>
            </th>

            <th>
              <VBox>
                <div>
                  {T('이상혈당 발생(건)')}
                </div>
              </VBox>
            </th>

            <th>
              <VBox>
                <div>
                  {T('이상혈당 발생(%)')}
                </div>
              </VBox>
            </th>

            <th>
              <div>
                <VBox>
                  <div>
                    {T('평균 혈당상승폭')}
                  </div>
                </VBox>
                <div>
                  <SortButton {...{ order: increasingOrder, onClick: onIncreasingOrderChange }} />
                </div>
              </div>
            </th>
            <th>
              <VBox>
                <div>
                  {T('평균 최고혈당')}
                </div>
              </VBox>
            </th>

            <th>
              <VBox>
                <div>
                  {T('평균 섭취칼로리(kcal)')}
                </div>
              </VBox>
            </th>
            <th>
              <VBox>
                <div>
                  {T('평균 식사량(g)')}
                </div>
              </VBox>
            </th>
            <th>
              <VBox>
                <div>
                  {T('평균 탄수화물(g)')}
                </div>
              </VBox>
            </th>
            <th>
              <VBox>
                <div>
                  {T('평균 탄단지(g)')}
                </div>
              </VBox>
            </th>


          </tr>
        </thead>
        <tbody>
          {list.map((item, index) => {
            const {
              main_food_nm,
              intaken_cnt,
              avg_intaken_cal,
              avg_intaken_amt,
              carbo_intaken_amt,
              carbo_rate,
              protein_rate,
              fat_rate,
              postprand2h_avg_bldsugr_rise_w,
              postprand2h_avg_bldsugr_min,
              postprand2h_avg_bldsugr,
              postprand2h_avg_bldsugr_max,
              postprand2h_abnormal_cnt,
              postprand2h_abnormal_rate,
            } = item;
            return (
              <tr key={index}>
                <td>
                  <div>
                    {main_food_nm}
                  </div>
                </td>
                <td>
                  <div>
                    {intaken_cnt}
                  </div>
                </td>

                <td>
                  <div>
                    {postprand2h_abnormal_cnt}
                  </div>
                </td>

                <td>
                  <div>
                    {postprand2h_abnormal_rate}
                  </div>
                </td>

                <td>
                  <div>
                    {postprand2h_avg_bldsugr_rise_w === 0 ? '' : Math.sign(postprand2h_avg_bldsugr_rise_w) < 0 ? '-' : '+'}{Math.abs(postprand2h_avg_bldsugr_rise_w)}
                  </div>
                </td>
                <td>
                  <div {...ex(['BASE_COLOR', CHECK_COLORS('B', postprand2h_avg_bldsugr)])}>
                    {postprand2h_avg_bldsugr}
                  </div>
                </td>

                <td>
                  <div>
                    {avg_intaken_cal}
                  </div>
                </td>
                <td>
                  <div>
                    {avg_intaken_amt}
                  </div>
                </td>
                <td>
                  <div>
                    {carbo_intaken_amt}
                  </div>
                </td>
                <td>
                  <div>
                    <CpfRatioBox
                      {...{
                        carbohydrate: carbo_rate,
                        protein: protein_rate,
                        fat: fat_rate,
                      }}
                    />
                  </div>
                </td>


              </tr>
            );
          })}
        </tbody>
      </table>
      <PagingIndicator
        {...{
          indicatorCount: 5,
          maxPage,
          page,
          onNPage,
          renderPrevButtons: defaultRenderPrevButtons,
          renderNextButtons: defaultRenderNextButtons,
        }}
      />
    </Section>
  );
}
